import React from "react";
import { useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import Header from "../components/HomePage/header";
import OrderSummary from "./OrderSummary";
import { HashLoader } from "react-spinners"; // Importing the spinner

export default function CheckoutForm({ subscriptionData }) {
  const [loading, setLoading] = useState(false); // To control loading state

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#aab7c4",
        },
        // Add the border styles here
        border: "1px solid #ced4da",
        borderRadius: "4px",
        padding: "10px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a",
        // Add the invalid border styles here
        border: "1px solid #fa755a",
      },
    },
    hidePostalCode: true,
  };

  //  Calculation of Price
  const base_Price = subscriptionData?.price; // Base price of the subscription plan
  const plan_Duration = subscriptionData.duration; // Duration of the subscription plan
  const base_gst = 18; // GST rate in percentage

  // Calculate the total price for the given duration
  const total_Price_Before_Gst = base_Price * plan_Duration;

  // Calculate the GST amount
  const plan_Gst = (total_Price_Before_Gst * base_gst) / 100;

  // Calculate the final total price including GST
  const total_Price = total_Price_Before_Gst + plan_Gst;

  //Stipe Integer Pricing

  const strip_Price = total_Price * 100;

  console.log("Total Price Including Gst:", total_Price);

  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstname, setFirstName] = useState("");
  const [lastname, setLastName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [step, setStep] = useState(0); // Track the current step (0 - 4)
  const [isProcessing, setIsProcessing] = useState(false); // Track if processing has started

  const handleCheckout = async (event) => {
    event.preventDefault();
    setLoading(true); // Start loading spinner
    setErrorMessage(""); // Clear any previous error message
    setIsProcessing(true); // Start processing

    setStep(1); // Start from step 1

    if (!stripe || !elements) {
      console.error("Stripe.js has not loaded yet.");
      setLoading(false);
      setErrorMessage("Stripe has not loaded yet. Please try again later.");
      return;
    }

    try {
      // Step 1: Send user registration data to your server
      const userResponse = await fetch("/api/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password, firstname, lastname }),
      });

      if (!userResponse.ok) {
        setErrorMessage(
          "An account with this email already exists. Please log in."
        );
        return;
      }

      setStep(2); // Move to step 2 (Stripe Customer Creation)

      // Step 2: Create a customer on Stripe
      const stripeCustomerResponse = await fetch(
        "/api/create-stripe-customer",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username }),
        }
      );

      if (!stripeCustomerResponse.ok) {
        setErrorMessage(
          "Customer creation failed. Error code: cus_create_failed_001. For assistance, please contact support at support@prepnexa.com"
        );
        return;
      }

      const stripeCustomerData = await stripeCustomerResponse.json();
      console.log("Stripe Customer Data:", stripeCustomerData);
      setStep(3); // Move to step 3 (Tokenizing Card)

      // Step 3: Tokenize card details with Stripe.js
      const cardElement = elements.getElement(CardElement);
      const { token, error: tokenError } = await stripe.createToken(
        cardElement
      );

      if (tokenError) {
        console.error("Error creating card token:", tokenError.message);
        setErrorMessage(
          "Check the card details. Please ensure the card information is correct."
        );
        return;
      }

      console.log("Card Token:", token);
      setStep(4); // Move to step 4 (Processing Payment)

      // Step 4: Send card token and customer ID to your server for payment processing
      const paymentResponse = await fetch("/api/process-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tokenId: token.id,
          customerId: stripeCustomerData.customerId,
          amount: strip_Price,
          planId: subscriptionData.planID,
          location: token.client_ip,
          paymentType: token.type,
          username,
        }),
      });

      if (!paymentResponse.ok) {
        setErrorMessage(
          "Payment processing failed. Please try again or contact support if the issue persists."
        );
        return;
      }

      const paymentData = await paymentResponse.json();
      console.log("Payment Data:", paymentData);

      // Handle 3D Secure authentication
      if (paymentData.requiresAction) {
        const { error: confirmError } = await stripe.confirmCardPayment(
          paymentData.clientSecret
        );

        if (confirmError) {
          console.error("Error confirming payment:", confirmError.message);
          setErrorMessage(
            "Payment processing failed. Please try again or contact support if the issue persists."
          );
          return;
        }
      }
      setStep(5); // Move to final step (Subscription Creation)

      // Step 5: Create a subscription for the user
      const subscribeResponse = await fetch("/api/subscribes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          customerId: stripeCustomerData.customerId,
          planId: subscriptionData.planID,
          plan: subscriptionData.plan,
          duration: subscriptionData.duration,
          price: strip_Price,
          username,
        }),
      });

      console.log("PlanID:", subscriptionData.planID);

      const subscriptionResponse = await subscribeResponse.json();
      console.log("Subscription Data:", subscriptionResponse);

      // Handle success or failure for payment and subscription separately
      let role;
      if (paymentData.success && subscriptionResponse.success) {
        switch (subscriptionData.plan) {
          case "Basic":
            role = 6;
            break;
          case "Starter":
            role = 5;
            break;
          case "Premium":
            role = 4;
            break;
          default:
            role = 7; // Default role if the plan is unknown
        }
      } else {
        role = 7; // Role to set if payment or subscription fails
      }

      try {
        // Update user roles based on the subscription plan
        const response = await fetch(`/api/update-role`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role,
            username,
            subscriptionId: subscriptionResponse.subscriptionId,
          }),
        });

        if (!response.ok) {
          setErrorMessage(
            "Failed to update user. Error code: 1023. Please contact Customer Care."
          );
          return;
        }

        if (paymentData.success && subscriptionResponse.success) {
          // Redirect to success page
          navigate("/success");
        } else {
          setErrorMessage("Payment or subscription failed. Role updated to 7.");
        }
      } catch (error) {
        setErrorMessage(
          "Error updating user. Error code: 1023. Please contact Customer Care."
        );
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again later.");
      setIsProcessing(false); // Stop processing on error
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };
  const steps = [
    "Start Payment",
    "Registration",
    "Processing ",
    "Almost There",
    "Payment Processing",
  ];

  return (
    <div>
      <Header />
      <div class="grid sm:px-10 lg:grid-cols-2 lg:px-20 xl:px-32">
        <OrderSummary subscriptionData={subscriptionData} />

        <div class="mt-10 bg-gray-50 px-4 pt-8 lg:mt-0">
          <p class="text-xl font-medium">Payment Details</p>
          <p class="text-gray-400">
            Complete your order by providing your payment details.
          </p>

          <form onSubmit={handleCheckout}>
            <label for="email" class="mt-4 mb-2 block text-sm font-medium">
              Email
            </label>
            <div class="relative">
              <input
                type="text"
                id="email"
                name="username"
                className="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                placeholder="your.email@gmail.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
            </div>
            <div class="">
              <label for="email" class="mt-4 mb-2 block text-sm font-medium">
                First Name
              </label>
              <div class="relative">
                <input
                  type="text"
                  id="firstname"
                  name="firstname"
                  class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Your. FirstName"
                  value={firstname}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <label for="email" class="mt-4 mb-2 block text-sm font-medium">
              LastName
            </label>
            <div class="relative">
              <input
                type="text"
                id="lastname"
                name="lastname"
                class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Last Name"
                value={lastname}
                onChange={(e) => setLastName(e.target.value)}
              />
              <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>
            </div>
            <label for="email" class="mt-4 mb-2 block text-sm font-medium">
              Password
            </label>
            <div class="relative">
              <input
                type="password"
                id="password"
                name="password"
                class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                placeholder="***********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  />
                </svg>
              </div>

              <label for="email" class="mt-4 mb-2 block text-sm font-medium">
                Confirm Password
              </label>
              <div class="relative">
                <input
                  type="password"
                  id="password"
                  name="password"
                  class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="***********"
                />
                <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
              </div>

              <label
                for="card-holder"
                class="mt-4 mb-2 block text-sm font-medium"
              >
                Card Holder
              </label>
              <div class="relative">
                <input
                  type="text"
                  id="card-holder"
                  name="card-holder"
                  class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm uppercase shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Your full name here"
                />
                <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
                    />
                  </svg>
                </div>
              </div>
              <label for="card-no" class="mt-4 mb-2 block text-sm font-medium">
                Card Details
              </label>
              {/* Stripe Card Element */}
              <div
                style={{
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  padding: "10px",
                  marginBottom: "10px",
                }}
              >
                <CardElement options={CARD_ELEMENT_OPTIONS} />
              </div>
              <label
                for="billing-address"
                class="mt-4 mb-2 block text-sm font-medium"
              >
                Billing Address
              </label>
              <div class="flex flex-col sm:flex-row">
                <div class="relative flex-shrink-0 sm:w-7/12">
                  <input
                    type="text"
                    id="billing-address"
                    name="billing-address"
                    class="w-full rounded-md border border-gray-200 px-4 py-3 pl-11 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Street Address"
                  />
                  <div class="pointer-events-none absolute inset-y-0 left-0 inline-flex items-center px-3">
                    <img
                      class="h-4 w-4 object-contain"
                      src="https://flagpack.xyz/_nuxt/4c829b6c0131de7162790d2f897a90fd.svg"
                      alt=""
                    />
                  </div>
                </div>
                <select
                  type="text"
                  name="billing-state"
                  class="w-full rounded-md border border-gray-200 px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="State">State</option>
                </select>
                <input
                  type="text"
                  name="billing-zip"
                  class="flex-shrink-0 rounded-md border border-gray-200 px-4 py-3 text-sm shadow-sm outline-none sm:w-1/6 focus:z-10 focus:border-blue-500 focus:ring-blue-500"
                  placeholder="ZIP"
                />
              </div>
            </div>
            {errorMessage && (
              <div className="error-message">
                <p style={{ color: "red" }}>{errorMessage}</p>
              </div>
            )}
            {isProcessing && !errorMessage && (
              <div className="w-full mt-4">
                {/* Progress Container */}
                <div className="relative pt-1">
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                    {" "}
                    {/* Add overflow-hidden here */}
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated h-2.5 bg-gray-800 rounded-full"
                      style={{
                        width: `${(step / (steps.length - 1)) * 100}%`,
                        transition: "width 0.5s ease-in-out", // Optional smooth transition for progress bar
                      }}
                      aria-valuenow={(step / (steps.length - 1)) * 100}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  </div>
                </div>
                {/* Step Description */}
                <p className="text-lg text-center text-gray-700 mt-2">
                  {steps[step]}
                </p>
              </div>
            )}

            <button
              type="submit"
              className={`mt-4 mb-8 w-full rounded-md px-6 py-3 font-medium text-white transition-opacity duration-300 ease-in-out ${
                loading
                  ? "opacity-50 cursor-not-allowed bg-gray-700"
                  : "bg-gray-900"
              }`}
              disabled={loading} // Disable button during loading
            >
              {loading ? (
                <div className="flex justify-center items-center">
                  <HashLoader size={20} color="#fff" />
                </div>
              ) : (
                `Pay ${total_Price}`
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
